import {Component, OnInit, ViewChild} from '@angular/core';
import {Pageable} from '../../../_domains/spring/pageable';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ConfirmationService, MenuItem, SelectItem} from 'primeng/api';
import {TypeAdmin, User} from '../../../_domains/UITypes';
import {UIUtils} from '../../../_utils/ui-utils';
import {DataAdminService} from '../_services/data-admin.service';
import {finalize} from 'rxjs/operators';
import {AppMessageService} from '../../../_services/app-message.service';
import {UtlListProjectsComponent} from '../utl-list-projects/utl-list-projects.component';
import {UtlUserProjectsComponent} from '../utl-user-projects/utl-user-projects.component';
import {TranslateService} from '@ngx-translate/core';
import {ConfigService} from "../../../_services/config.service";
import {TableLazyLoadEvent} from "primeng/table";

@Component({
  selector: 'app-admin-users',
  templateUrl: './users.component.html'
})
export class UsersComponent implements OnInit {
  @ViewChild(UtlListProjectsComponent, {static: false}) listAddProjectsCmp;
  @ViewChild(UtlUserProjectsComponent, {static: false}) listUserProjectsCmp;

  readonly userForm: UntypedFormGroup;
  users: Pageable;
  loading: boolean = false;
  private currentPage: number = 0;
  private sorting: string;
  private tableFilter: any;

  selected!: User;
  items: SelectItem[] = [];
  types: SelectItem[] = [];

  showDlgAddProjects: boolean = false;
  showDlgListProjects: boolean = false;
  showDlgUserRecord: boolean = false;
  showDlgLogs: boolean;
  private readonly viewLogsMenu: MenuItem[];
  showDlgLogsType: number;
  editMode!: string;
  currentUser!: User;

  constructor(private dataService: DataAdminService,
              private messageService: AppMessageService,
              private confirmationService: ConfirmationService,
              public configService: ConfigService,
              private fb: UntypedFormBuilder,
              private translate: TranslateService) {
    this.users = new Pageable();
    this.userForm = fb.group({
      id: {value: '', disabled: true},
      keycloakId: {value: '', disabled: true},
      firstName: [null, [Validators.required, Validators.maxLength(256), Validators.pattern('^[^\\s]+(\\s.*)?$')]],
      lastName: [null, [Validators.required, Validators.maxLength(256), Validators.pattern('^[^\\s]+(\\s.*)?$')]],
      psw: '',
      email: [null, [Validators.required, Validators.maxLength(96)]],
      phone: [null, Validators.maxLength(256)],
      variables: '',
      isAdmin: [0],
      enabled: [null],
      sendActivationEmail: null,
      accessTokenValidity: ''
    });
    this.viewLogsMenu = [
      {
        label: 'Logons', command: () => {
          this.listLogConnects(this.selected);
        }
      },
      {
        label: 'Actions', command: () => {
          this.listLogPrivate(this.selected);
        }
      }
    ];
  }

  ngOnInit() {
    this.loading = true;
    this.types.push({label: 'All', value: ''});
    this.types.push({label: 'None', value: '0'});
    this.types.push({label: 'System administrator', value: '1'});
    this.types.push({label: 'API User', value: '4'});
  }

  private loadData(filter?: string) {
    if (this.tableFilter) {
      let search = UIUtils.getFilterStr(this.tableFilter);
      if (search.length > 0) {
        filter += `&search=${search}`;
      }
    }
    this.loading = true;
    this.dataService.getAdmListUsers(filter)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (data) => this.users = data,
        error: (error) => this.messageService.showMessage(error, 'Error receiving data')
      });
  }

  listLogConnects(row: any) {
    this.showDlgLogsType = 1;
    this.showDlgLogs = true;
  }

  listLogPrivate(row: any) {
    this.showDlgLogsType = 2;
    this.showDlgLogs = true;
  }

  openDlgUserRecord(isCreate: boolean, user?: User) {
    if (isCreate) {
      this.editMode = 'ADD';
      this.currentUser = {};
    } else {
      if (!user) {
        return;
      }
      this.editMode = 'EDIT';
      this.currentUser = {...user};
    }
    this.showDlgUserRecord = true;
  }

  openDlgListProjects() {
    this.showDlgAddProjects = false;
    this.showDlgListProjects = true;
    this.listUserProjectsCmp.refresh(this.selected.id);
  }

  handlePageChange(event: TableLazyLoadEvent) {
    this.users.content = [];
    this.tableFilter = event.filters;
    this.currentPage = (event.rows == 0) ? 0 : Math.floor(event.first / event.rows);
    this.sorting = event.sortField && `&sort=${event.sortField},${event.sortOrder == 1 ? 'asc' : 'desc'}`;
    this.loadData('?page=' + this.currentPage + this.sorting);
  }

  handleRowSelect(event) {
    this.selected = event.data;
    this.currentUser = {...this.selected};
  }

  handleDblRowClick(event, row: User) {
    this.openDlgUserRecord(false, row);
  }

  private reloadData() {
    this.currentPage = 0;
    this.loadData('?page=' + this.currentPage + (this.sorting || ''));
    this.selected = null;
    this.userForm.reset();
  }

  save(value: any) {
    this.loading = true;
    const user: User = {
      id: value.id,
      missionCode: value.missionCode.trim(),
      country: value.country.trim(),
      city: value.city.trim(),
      institutionType: value.institutionType.trim(),
      institutionName: value.institutionName.trim(),
      firstName: value.firstName.trim(),
      lastName: value.lastName.trim(),
      typeAdmin: value.isAdmin,
      email: value.email.toLowerCase().trim(),
      phone: value.phone,
      variables: value.variables,
      accountLocked: value.enabled === false,
    };
    if (value.isAdmin === TypeAdmin.API) {
      user.psw = value.psw;
      user.accessTokenValidity = value.accessTokenValidity;
    }

    const sendActivationEmail = value.sendActivationEmail;
    (!user.id ? this.dataService.createUser(user) : this.dataService.saveUser(user))
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (data) => {
          this.showDlgUserRecord = false;
          this.messageService.showSuccess(user.id == null ?
            this.translate.instant('message.user.add.success') : this.translate.instant('message.user.save.success'));
          if (user.variables && user.variables !== data.variables
            && user.variables.length !== data.variables?.length) {
            this.messageService.showInfo(this.translate.instant('message.user.variables.check'));
          }
          this.reloadData();
          if (sendActivationEmail) {
            this.doSendVerifyEmail(data.id, false);
          }
        },
        error: (error) => this.messageService.showMessage(error, 'Error')
      });
  }

  delete() {
    if (!this.selected?.id) return;
    this.confirmationService.confirm({
      message: this.translate.instant('message.user.delete.confirmation'),
      header: this.translate.instant('message.header.confirmation'),
      icon: 'fa fa-question-circle',
      accept: () => {
        this.loading = true;
        this.dataService.deleteUser(this.selected.id)
          .pipe(finalize(() => this.loading = false))
          .subscribe({
            next: (data) => {
              this.reloadData();
              this.messageService.showSuccess(this.translate.instant('message.user.delete.success'));
            },
            error: (error) => this.messageService.showMessage(error, this.translate.instant('message.header.error'))
          });
      }
    });
  }

  sendVerifyEmail() {
    if (!this.selected?.keycloakId) return;
    this.confirmationService.confirm({
      message: this.translate.instant('message.user.sendVerifyEmail.confirmation'),
      header: this.translate.instant('message.header.confirmation'),
      icon: 'fa fa-question-circle',
      accept: () => this.doSendVerifyEmail(this.selected.id, false)
    });
  }

  private doSendVerifyEmail(userId: string, force: boolean) {
    this.loading = true;
    this.dataService.sendVerifyEmail(userId, force)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (resp) => {
          if (!force && resp?.status === -1) {
            this.confirmationService.confirm({
              message: this.translate.instant('message.user.sendVerifyEmail.confirmationAgain'),
              header: this.translate.instant('message.header.confirmation'),
              icon: 'fa fa-question-circle',
              accept: () => this.doSendVerifyEmail(userId, true)
            });
          } else {
            this.reloadData();
            this.messageService.showSuccess(this.translate.instant('message.user.sendVerifyEmail.success'));
          }
        },
        error: (error) =>
          this.messageService.showMessage(error, this.translate.instant('message.header.error'))
      });
  }

  updateActivationDates() {
    this.confirmationService.confirm({
      message: this.translate.instant('message.user.updateActivationDates.confirmation'),
      header: this.translate.instant('message.header.confirmation'),
      icon: 'fa fa-question-circle',
      accept: () => {
        this.loading = true;
        this.dataService.updateActivationDates()
          .pipe(finalize(() => this.loading = false))
          .subscribe({
            next: () => {
              this.reloadData();
              this.messageService.showSuccess(this.translate.instant('message.user.updateActivationDates.success'));
            },
            error: (error) =>
              this.messageService.showMessage(error, this.translate.instant('message.header.error'))
          });
      }
    });
  }

  get menu(): MenuItem[] {
    return this.viewLogsMenu;
  }
}
